import React from 'react';
import ReactDOM from 'react-dom/client';
import './fonts.css';
import './index.css';
import App from './App';
import { migrateStatsV2, getSavedTheme } from './utils';
import { THEMES, SANS, applyThemeToDocument } from './themes';
import { startAppUpdates } from './appUpdate';
import { startErrorLog, reportError } from './errorLog';

import { t } from './i18n';
import { panelSurface } from './ui/surface';
// Журнал ошибок — первым, чтобы попали и ошибки запуска.
startErrorLog();
// Тема на странице (шрифт, фон, цвет строки состояния) — до первого рендера, чтобы не мигнуло светлым.
applyThemeToDocument(getSavedTheme());
migrateStatsV2();
// Приложение на телефоне: подтвердить запуск этой версии и проверить новую (на вебе ничего не делает).
startAppUpdates();

// Error boundary — вместо белого экрана при крэше показывает сообщение и кнопку перезагрузки.
class AppErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    reportError('crash', error, info?.componentStack || '');
    console.error('[App crash]', error, info);
  }
  render() {
    if (this.state.error) {
      const P = THEMES[getSavedTheme()] || THEMES.light;
      const text = String(this.state.error?.stack || this.state.error?.message || this.state.error);
      const btn = (primary) => ({ minHeight: 40, padding: '0 16px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: SANS,
        background: primary ? P.blue : 'transparent', color: primary ? '#fff' : P.text, border: `1px solid ${primary ? P.blue : P.border}` });
      const copy = () => { try { navigator.clipboard?.writeText(text); this.setState({ copied: true }); } catch { /* нет доступа к буферу */ } };
      return (
        <div style={{ padding: 16, fontFamily: SANS, color: P.text, background: P.backdrop, minHeight: '100vh', boxSizing: 'border-box' }}>
          <div role="alert" style={{ maxWidth: 560, margin: '40px auto', ...panelSurface(P), padding: 24, borderRadius: 12, boxSizing: 'border-box' }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 600 }}>{t("Что-то сломалось")}</h2>
            <p style={{ margin: '0 0 12px', color: P.mid, fontSize: 14, lineHeight: 1.5 }}>
              {t("Произошла ошибка в приложении. Обновите страницу; если ошибка повторится — скопируйте текст ниже и пришлите его нам.")}
            </p>
            <pre style={{ background: P.surface, padding: 12, borderRadius: 8, fontSize: 12, color: P.danger, overflow: 'auto', maxHeight: 260, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
              {text}
            </pre>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <button onClick={() => window.location.reload()} style={btn(true)}>{t("Перезагрузить")}</button>
              <button onClick={copy} style={btn(false)}>{this.state.copied ? t("Скопировано") : t("Скопировать текст ошибки")}</button>
              <button onClick={() => this.setState({ error: null, copied: false })} style={btn(false)}>{t("Попробовать продолжить")}</button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </React.StrictMode>
);

// ── PWA Service Worker (только на вебе, не в Capacitor) ──────────────────────
// Только в собранной версии: при `npm start` SW кэшировал бы код разработки и правки не было бы видно.
if (
  process.env.NODE_ENV === 'production' &&
  'serviceWorker' in navigator &&
  !(window.Capacitor?.isNativePlatform?.())
) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', {
        scope: '/',
        // updateViaCache: 'none' — браузер ВСЕГДА запрашивает sw.js с сервера,
        // игнорируя HTTP-кэш. Это ключевое для быстрого обновления дизайна.
        updateViaCache: 'none',
      })
      .then((reg) => {
        console.log('[SW] registered, scope:', reg.scope);

        // Проверяем обновления при каждом открытии страницы
        reg.update();

        // И дополнительно каждые 30 минут пока вкладка открыта
        setInterval(() => reg.update(), 30 * 60 * 1000);

        // Когда новый SW готов — активируем его немедленно без перезагрузки
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Новый SW установлен — говорим ему активироваться сейчас
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch((err) => console.warn('[SW] registration failed:', err));

    // Когда вышла новая версия (SW сменился) — перезагружаемся, чтобы её подхватить.
    // Но не при самой первой установке SW и не посреди прослушивания: раньше страница
    // перезагружалась сразу, обрывая аудио. Теперь — когда вкладка свёрнута и ничего
    // не играет; иначе новая версия просто откроется в следующий раз.
    const hadController = !!navigator.serviceWorker.controller;
    let refreshing = false;
    const playing = () => Array.from(document.querySelectorAll('audio,video')).some((m) => !m.paused && !m.ended);
    const reloadWhenIdle = () => {
      if (refreshing || playing() || document.visibilityState !== 'hidden') return;
      refreshing = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController || refreshing) return;
      document.addEventListener('visibilitychange', reloadWhenIdle);
      setInterval(reloadWhenIdle, 60 * 1000);
    });
  });
}

